import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { useMemo } from 'react';

import { SectionTitle } from 'modules/shared/components';
import { formatDateTime } from 'modules/shared/utils';
import { DOCUMENT_SLOTS } from '../../shared/constants';
import { DOCUMENT_COLLECTION_LABELS } from '../constants';

function normalizeBackendDocuments(documents = []) {
  return documents.reduce((acc, doc) => {
    acc[doc.name] = { backendFile: doc };
    return acc;
  }, {});
}

function buildOrderedDocuments({ source, slots }) {
  const predefined = slots.map((slot) => {
    const key = slot.candidateLabel ?? slot.name;
    return {
      key,
      label: key,
      required: slot.required,
      ...(source[key] ?? {}),
    };
  });

  const additional = Object.keys(source)
    .filter((key) => !slots.some((slot) => slot.name === key || slot.candidateLabel === key))
    .map((key) => ({
      key,
      label: key,
      required: false,
      ...(source[key] ?? {}),
    }));

  return [...predefined, ...additional];
}

function resolvePreviewUrl({ backendFile }) {
  return backendFile?.url ?? null;
}

export default function DocumentCollectionView({ data = [] }) {
  const dataSource = useMemo(() => normalizeBackendDocuments(data), [data]);

  const orderedDocuments = useMemo(
    () =>
      buildOrderedDocuments({
        source: dataSource,
        slots: DOCUMENT_SLOTS,
      }),
    [dataSource],
  );

  return (
    <div className='column gap-3'>
      <SectionTitle title={DOCUMENT_COLLECTION_LABELS.TITLE} />

      <div className='column gap-2'>
        {orderedDocuments.map((doc) => (
          <DocumentRow key={doc.key} doc={doc} />
        ))}
      </div>
    </div>
  );
}

function DocumentRow({ doc }) {
  const previewUrl = useMemo(() => resolvePreviewUrl({ backendFile: doc.backendFile }), [doc]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <span>
        {doc.label}
        {doc.required && <span className='required'>*</span>}
      </span>

      <span className='text--secondary'>
        {doc.backendFile
          ? `Uploaded by ${doc.backendFile.uploadedBy} · ${formatDateTime(
              doc.backendFile.uploadedDate,
            )}`
          : DOCUMENT_COLLECTION_LABELS.NOT_UPLOADED}
      </span>

      <div className='row gap-3 justify-self-end'>
        {previewUrl && (
          <MuiLink href={previewUrl} target='_blank' rel='noreferrer'>
            view
          </MuiLink>
        )}
      </div>
    </Box>
  );
}
